import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";

export type TripClassification = "completed" | "partial" | "non_trip";

// What the ignition codes are standing in for, and the only thing left to
// go on when they are missing. Anything that reported a speed went
// somewhere, whatever its event codes say.
const hasMovement = (tripPoints: VehicleMapDataPoint[]): boolean =>
  tripPoints.some((point) => (point.speedkph ?? 0) > 0);

// both ignition points -> completed, one -> partial, neither -> whether it
// moved. Shared, so the compare cards label trips the same way the rows do.
export const classifyTrip = (
  tripPoints: VehicleMapDataPoint[]
): TripClassification => {
  const hasIgnitionOn = tripPoints.some(
    (point) => point.statuscode === VehicleStatusCode.STATUS_IGNITION_ON
  );
  const hasIgnitionOff = tripPoints.some(
    (point) => point.statuscode === VehicleStatusCode.STATUS_IGNITION_OFF
  );
  if (hasIgnitionOn && hasIgnitionOff) return "completed";
  if (hasIgnitionOn || hasIgnitionOff) return "partial";
  // A journey can arrive carrying neither boundary. The map-data endpoint
  // throws away every event without a GPS fix before it groups them into
  // trips, and an ignition-on after a vehicle has sat overnight is exactly
  // the event most likely not to have a fix yet - so the first run of the
  // day can lose the one code that proves it is a run. Judged on ignition
  // alone it then looks like a vehicle that reported without moving, which
  // the trip list hides by default: a trip plainly unfolding on the map,
  // listed nowhere.
  return hasMovement(tripPoints) ? "partial" : "non_trip";
};

// a trip that never went anywhere. Derived from the classification rather
// than repeating its rule, so the two can't drift apart.
export const isNonTrip = (tripPoints: VehicleMapDataPoint[]): boolean =>
  classifyTrip(tripPoints) === "non_trip";

// keys under the vehicleMap.tripDetails namespace
export const TRIP_CLASSIFICATION_LABEL_KEY: Record<TripClassification, string> =
  {
    completed: "completed_trip",
    partial: "partial_trip",
    non_trip: "non_trip",
  };
